import React, { useMemo, useState } from 'react';
import { gql, useApolloClient, useMutation, useQuery } from '@apollo/client';
import { AlertCircle, CheckCircle2, ChevronLeft, ChevronRight, Download, RefreshCw, Settings2, Upload, XCircle } from 'lucide-react';
import * as XLSX from 'xlsx';

import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { toast } from '@/components/ui/toast';
import { UnifiedTable, UnifiedTableColumn } from '@/components/ui/unifiedTable/UnifiedTable';
import { cn } from '@/lib/utils';

const PUBLIC_DEFENDERS_LIST_PRE_CANDIDATES = gql`
  query PublicDefendersListPreCandidates($dateFrom: String, $dateTo: String, $page: Int, $pageSize: Int) {
    publicDefendersListPreCandidates(dateFrom: $dateFrom, dateTo: $dateTo, page: $page, pageSize: $pageSize) {
      totalRows
      page
      pageSize
      rows {
        id
        booking
        last4main
        lastname
        firstname
        dob
        sex
        caseNumber
        facility
        module
        mental
        arrestDate
        plevel
        included
        exclusionReason
        cases
        charges
        hohReportDate
        pLevelReportDate
        firstSeenAt
        lastSeenAt
        isNew
        decisionOverride
        decisionByUsername
        decisionAt
      }
    }
  }
`;

const SET_PUBLIC_DEFENDERS_LIST_DECISION = gql`
  mutation SetPublicDefendersListDecision($id: Int!, $included: Boolean!) {
    setPublicDefendersListDecision(id: $id, included: $included) { id included exclusionReason decisionOverride decisionByUsername decisionAt }
  }
`;

const REFERRAL_UNACCEPTABLE_CHARGES = gql`
  query ReferralUnacceptableCharges {
    referralUnacceptableCharges {
      id
      charge
      note
      active
    }
  }
`;

const PROCESS_PUBLIC_DEFENDERS_LIST = gql`
  mutation ProcessPublicDefendersListPreCandidates($input: PublicDefendersListProcessInput!) {
    processPublicDefendersListPreCandidates(input: $input) {
      processedCount
      insertedCount
      updatedCount
      includedCount
      excludedCount
      badChargeExcludedCount
      pLevelExcludedCount
      duplicateCandidateExcludedCount
      duplicateBookingExcludedCount
      message
    }
  }
`;

type ChargeRule = {
  id: number;
  charge: string;
  note?: string | null;
  active?: boolean | null;
};

type PublicDefendersListRow = {
  id: number;
  booking?: string | null;
  last4main?: string | null;
  lastname?: string | null;
  firstname?: string | null;
  dob?: string | null;
  sex?: string | null;
  caseNumber?: string | null;
  facility?: string | null;
  module?: string | null;
  mental?: string | null;
  arrestDate?: string | null;
  plevel?: string | null;
  included?: boolean | null;
  exclusionReason?: string | null;
  cases?: string[] | null;
  charges?: string[] | null;
  hohReportDate?: string | null;
  pLevelReportDate?: string | null;
  firstSeenAt?: string | null;
  lastSeenAt?: string | null;
  isNew?: boolean | null;
  decisionOverride?: boolean | null;
  decisionByUsername?: string | null;
  decisionAt?: string | null;
};

type SheetWorkbook = {
  fileName: string;
  workbook: XLSX.WorkBook;
  sheetNames: string[];
};

type ParsedCandidate = {
  booking: string;
  last4main: string;
  lastname: string;
  firstname: string;
  dob: string;
  sex: string;
  caseNumber: string;
  facility: string;
  module: string;
  mental: string;
  arrestDate: string;
  plevel: string;
  frontendIncluded: boolean;
  frontendReasons: string[];
  cases: string[];
  charges: string[];
  hohRowsJson: string;
  pLevelRowJson: string;
};

const DEFAULT_PAGE_SIZE = 20;
const EXPORT_PAGE_SIZE = 100;
const COLUMN_STORAGE_KEY = 'public-defenders-list-visible-columns';

const cleanCell = (value: unknown) =>
  `${value ?? ''}`.replace(/\u00a0/g, ' ').replace(/\s+/g, ' ').trim();

const normalizeHeader = (value: unknown) => cleanCell(value).toLowerCase().replace(/[^a-z0-9]/g, '');

const normalizeDigits = (value: unknown) => cleanCell(value).replace(/\D/g, '');

const normalizeCharge = (value: unknown) => cleanCell(value).toLowerCase().replace(/\s+/g, ' ');

const normalizePLevel = (value: unknown) => {
  const raw = cleanCell(value).toUpperCase().replace(/\s+/g, '');
  if (!raw) return '';
  if (raw === '3' || raw === 'P3' || raw.includes('P3')) return 'P3';
  if (raw === '4' || raw === 'P4' || raw.includes('P4')) return 'P4';
  return raw;
};

const dedupe = (values: string[]) => {
  const seen = new Set<string>();
  const result: string[] = [];
  values.forEach((value) => {
    const clean = cleanCell(value);
    const key = clean.toLowerCase();
    if (!clean || seen.has(key)) return;
    seen.add(key);
    result.push(clean);
  });
  return result;
};

const monthMap: Record<string, string> = {
  jan: '01',
  january: '01',
  feb: '02',
  february: '02',
  mar: '03',
  march: '03',
  apr: '04',
  april: '04',
  may: '05',
  jun: '06',
  june: '06',
  jul: '07',
  july: '07',
  aug: '08',
  august: '08',
  sep: '09',
  sept: '09',
  september: '09',
  oct: '10',
  october: '10',
  nov: '11',
  november: '11',
  dec: '12',
  december: '12',
};

const normalizeYear = (year: string) => {
  if (year.length !== 2) return year;
  return Number(year) > 30 ? `19${year}` : `20${year}`;
};

const normalizeDate = (value: unknown): string => {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.toISOString().slice(0, 10);
  }

  const raw = cleanCell(value);
  if (!raw) return '';

  const iso = raw.match(/^(\d{4})[/-](\d{1,2})[/-](\d{1,2})$/);
  if (iso) {
    return `${iso[1]}-${iso[2].padStart(2, '0')}-${iso[3].padStart(2, '0')}`;
  }

  const numeric = raw.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{2,4})$/);
  if (numeric) {
    const year = normalizeYear(numeric[3]);
    return `${year}-${numeric[1].padStart(2, '0')}-${numeric[2].padStart(2, '0')}`;
  }

  const monthName = raw.match(/^(\d{1,2})[-\s/]([A-Za-z]{3,})[-\s/](\d{2,4})$/);
  if (monthName) {
    const month = monthMap[monthName[2].toLowerCase()];
    if (month) {
      return `${normalizeYear(monthName[3])}-${month}-${monthName[1].padStart(2, '0')}`;
    }
  }

  const parsed = new Date(raw);
  if (!Number.isNaN(parsed.getTime())) {
    return parsed.toISOString().slice(0, 10);
  }

  return '';
};

const formatDate = (value: unknown) => {
  const iso = normalizeDate(value);
  if (!iso) return '';
  const [year, month, day] = iso.split('-');
  return `${month}/${day}/${year}`;
};

const formatExclusionReason = (value: unknown) => {
  const reason = cleanCell(value);
  if (/^Booking already exists in ODR Housing candidates\.?$/i.test(reason)) {
    return 'Already an ODRH candidate.';
  }
  return reason;
};

const reportDateFromName = (value: string) => {
  const compact = value.match(/(?:^|[_\s-])(\d{2})(\d{2})(\d{2})(?:\D|$)/);
  if (compact) {
    return `20${compact[3]}-${compact[1]}-${compact[2]}`;
  }

  const separated = value.match(/(\d{1,2})[./-](\d{1,2})[./-](\d{2,4})/);
  if (separated) {
    return `${normalizeYear(separated[3])}-${separated[1].padStart(2, '0')}-${separated[2].padStart(2, '0')}`;
  }

  return '';
};

const readWorkbook = async (file: File): Promise<SheetWorkbook> => {
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, {
    type: 'array',
    cellDates: true,
  });

  return {
    fileName: file.name,
    workbook,
    sheetNames: workbook.SheetNames || [],
  };
};

const sheetRows = (workbook: XLSX.WorkBook, sheetName: string) => {
  const sheet = workbook.Sheets[sheetName];
  if (!sheet) return [] as unknown[][];
  return XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '', raw: false }) as unknown[][];
};

const findHeaderRow = (rows: unknown[][], requiredHeaders: string[]) => {
  for (let index = 0; index < rows.length; index += 1) {
    const normalized = new Set((rows[index] || []).map(normalizeHeader).filter(Boolean));
    const found = requiredHeaders.every((header) => normalized.has(header));
    if (found) return index;
  }
  return -1;
};

const rowObjects = (workbook: XLSX.WorkBook, sheetName: string, requiredHeaders: string[]) => {
  const rows = sheetRows(workbook, sheetName);
  const headerIndex = findHeaderRow(rows, requiredHeaders);
  if (headerIndex < 0) {
    throw new Error(`Could not find expected headers in ${sheetName}.`);
  }

  const headers = (rows[headerIndex] || []).map((header) => cleanCell(header));
  return rows.slice(headerIndex + 1).map((row) => {
    const object: Record<string, string> = {};
    headers.forEach((header, columnIndex) => {
      if (!header) return;
      object[header] = cleanCell((row || [])[columnIndex]);
    });
    return object;
  });
};

const findValue = (row: Record<string, string>, keys: string[]) => {
  const entries = Object.entries(row).map(([key, value]) => ({ key: normalizeHeader(key), value }));
  for (const key of keys) {
    const direct = entries.find((entry) => entry.key === key);
    if (direct && cleanCell(direct.value)) return cleanCell(direct.value);
  }
  for (const entry of entries) {
    if (keys.some((key) => entry.key.includes(key)) && cleanCell(entry.value)) {
      return cleanCell(entry.value);
    }
  }
  return '';
};

const parseHohCandidates = (workbook: XLSX.WorkBook, sheetName: string) => {
  const rows = rowObjects(workbook, sheetName, ['bkg', 'last4main', 'lname', 'fname']);
  const grouped = new Map<string, any>();

  rows.forEach((row) => {
    const booking = normalizeDigits(findValue(row, ['bkg', 'booking']));
    if (!booking) return;

    const last4main = normalizeDigits(findValue(row, ['last4main', 'mainlastfour', 'last4'])).slice(-4);
    const key = `${booking}|${last4main}`;
    const existing = grouped.get(key) || {
      booking,
      last4main,
      lastname: '',
      firstname: '',
      dob: '',
      sex: '',
      caseNumber: '',
      facility: '',
      module: '',
      mental: '',
      arrestDate: '',
      cases: [] as string[],
      charges: [] as string[],
      sourceRows: [] as Record<string, string>[],
    };

    const caseNumber = findValue(row, ['caseno', 'case']);
    const charge = findValue(row, ['charge']);

    existing.lastname = existing.lastname || findValue(row, ['lname', 'lastname']);
    existing.firstname = existing.firstname || findValue(row, ['fname', 'firstname']);
    existing.dob = existing.dob || normalizeDate(findValue(row, ['dob']));
    existing.sex = existing.sex || findValue(row, ['sex']);
    existing.caseNumber = existing.caseNumber || caseNumber;
    existing.facility = existing.facility || findValue(row, ['facility']);
    existing.module = existing.module || findValue(row, ['module']);
    existing.mental = existing.mental || findValue(row, ['mental']);
    existing.arrestDate = existing.arrestDate || normalizeDate(findValue(row, ['arrestdate']));
    existing.cases = dedupe([...existing.cases, caseNumber]);
    existing.charges = dedupe([...existing.charges, charge]);
    existing.sourceRows.push(row);

    grouped.set(key, existing);
  });

  return Array.from(grouped.values());
};

const parsePLevelRows = (workbook: XLSX.WorkBook, sheetName: string) => {
  const rows = rowObjects(workbook, sheetName, ['fin', 'plevel']);
  const mapped = new Map<string, any>();

  rows.forEach((row) => {
    const finDigits = normalizeDigits(findValue(row, ['fin']));
    const explicitBooking = normalizeDigits(findValue(row, ['bookingnumber', 'booking']));
    const explicitMain = normalizeDigits(findValue(row, ['mainlastfour', 'last4main', 'last4'])).slice(-4);
    const booking = explicitBooking || (finDigits.length >= 11 ? finDigits.slice(0, 7) : finDigits.slice(0, -4));
    const last4main = explicitMain || (finDigits.length >= 11 ? finDigits.slice(7, 11) : finDigits.slice(-4));
    if (!booking) return;

    mapped.set(`${booking}|${last4main}`, {
      booking,
      last4main,
      plevel: normalizePLevel(findValue(row, ['plevel'])),
      raw: row,
    });
  });

  return mapped;
};

const badChargeHits = (charges: string[], rules: ChargeRule[]) => {
  const normalizedRules = rules
    .map((rule) => ({ charge: cleanCell(rule.charge), normalized: normalizeCharge(rule.charge) }))
    .filter((rule) => rule.charge && rule.normalized);

  const hits: string[] = [];
  charges.forEach((charge) => {
    const normalizedCharge = normalizeCharge(charge);
    const match = normalizedRules.find((rule) =>
      normalizedCharge === rule.normalized ||
      (rule.normalized.length >= 5 && normalizedCharge.includes(rule.normalized))
    );
    if (match) hits.push(match.charge);
  });

  return dedupe(hits);
};

const parseAndScreenCandidates = ({
  hohWorkbook,
  hohSheet,
  pLevelWorkbook,
  pLevelSheet,
  chargeRules,
}: {
  hohWorkbook: SheetWorkbook;
  hohSheet: string;
  pLevelWorkbook: SheetWorkbook;
  pLevelSheet: string;
  chargeRules: ChargeRule[];
}): ParsedCandidate[] => {
  const hohRows = parseHohCandidates(hohWorkbook.workbook, hohSheet);
  const pLevelRows = parsePLevelRows(pLevelWorkbook.workbook, pLevelSheet);

  return hohRows.map((row) => {
    const pLevelRow = pLevelRows.get(`${row.booking}|${row.last4main}`) || null;
    const plevel = pLevelRow?.plevel || '';
    const reasons: string[] = [];
    const chargeHits = badChargeHits(row.charges, chargeRules);

    if (chargeHits.length > 0) {
      reasons.push(`Bad charge match: ${chargeHits.join(', ')}`);
    }

    if (!plevel) {
      reasons.push('Missing P-level match.');
    } else if (plevel !== 'P3' && plevel !== 'P4') {
      reasons.push('P-level is not P3 or P4.');
    }

    return {
      booking: row.booking,
      last4main: row.last4main,
      lastname: row.lastname,
      firstname: row.firstname,
      dob: row.dob,
      sex: row.sex,
      caseNumber: row.caseNumber,
      facility: row.facility,
      module: row.module,
      mental: row.mental,
      arrestDate: row.arrestDate,
      plevel,
      frontendIncluded: reasons.length === 0,
      frontendReasons: reasons,
      cases: row.cases,
      charges: row.charges,
      hohRowsJson: JSON.stringify(row.sourceRows),
      pLevelRowJson: JSON.stringify(pLevelRow?.raw || null),
    };
  });
};

const pillClass = (included?: boolean | null) =>
  included
    ? 'border-emerald-300 bg-emerald-50 text-emerald-800'
    : 'border-rose-300 bg-rose-50 text-rose-800';

const workflowButtonClass = 'h-7 w-[88px] justify-start gap-1.5 border-[var(--neutral-gray-40)] bg-[var(--neutral-white)] px-2 text-left text-xs font-medium text-[var(--neutral-gray-130)] shadow-none hover:border-[var(--neutral-gray-60)] hover:bg-[var(--neutral-gray-10)] hover:text-[var(--neutral-gray-140)]';

export const PublicDefendersListPage = () => {
  const apolloClient = useApolloClient();
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [hohWorkbook, setHohWorkbook] = useState<SheetWorkbook | null>(null);
  const [pLevelWorkbook, setPLevelWorkbook] = useState<SheetWorkbook | null>(null);
  const [hohSheet, setHohSheet] = useState('');
  const [pLevelSheet, setPLevelSheet] = useState('');
  const [processing, setProcessing] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [selectedRow, setSelectedRow] = useState<PublicDefendersListRow | null>(null);
  const [decisionRowId, setDecisionRowId] = useState<number | null>(null);
  const [visibleColumnKeys, setVisibleColumnKeys] = useState<string[]>(() => {
    try { return JSON.parse(localStorage.getItem(COLUMN_STORAGE_KEY) || 'null') || []; } catch { return []; }
  });

  const listVariables = {
    dateFrom: dateFrom || null,
    dateTo: dateTo || null,
    page,
    pageSize,
  };

  const { data, previousData, loading, refetch } = useQuery(PUBLIC_DEFENDERS_LIST_PRE_CANDIDATES, {
    variables: listVariables,
    fetchPolicy: 'cache-and-network',
  });

  const { data: chargeData } = useQuery(REFERRAL_UNACCEPTABLE_CHARGES, {
    fetchPolicy: 'cache-and-network',
  });

  const [processReports] = useMutation(PROCESS_PUBLIC_DEFENDERS_LIST);
  const [setDecision] = useMutation(SET_PUBLIC_DEFENDERS_LIST_DECISION);

  const rows: PublicDefendersListRow[] = Array.isArray(data?.publicDefendersListPreCandidates?.rows)
    ? data.publicDefendersListPreCandidates.rows
    : [];
  const totalRows = Number(
    data?.publicDefendersListPreCandidates?.totalRows
      ?? previousData?.publicDefendersListPreCandidates?.totalRows
      ?? 0
  );
  const totalPages = Math.max(1, Math.ceil(totalRows / pageSize));
  const canGoBack = page > 1;
  const canGoNext = page < totalPages;
  const chargeRules: ChargeRule[] = Array.isArray(chargeData?.referralUnacceptableCharges)
    ? chargeData.referralUnacceptableCharges
    : [];

  const defaultPLevelSheet = useMemo(() => {
    if (!pLevelWorkbook) return '';
    return pLevelWorkbook.sheetNames
      .filter((sheet) => normalizeHeader(sheet).includes('plevelreport'))
      .slice(-1)[0] || pLevelWorkbook.sheetNames[0] || '';
  }, [pLevelWorkbook]);

  const allColumns = useMemo<UnifiedTableColumn<PublicDefendersListRow>[]>(() => [
    {
      key: 'included',
      header: 'Status',
      width: '94px',
      accessor: (row) => (
        <span className={cn('inline-flex items-center gap-1 whitespace-nowrap rounded-[var(--border-radius-small)] border px-1.5 py-0.5 text-[11px] font-medium', pillClass(row.included))} title={row.decisionOverride != null ? `Overridden by ${row.decisionByUsername || 'unknown user'}` : undefined}>
          {row.included ? <CheckCircle2 className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
          {row.included ? 'Approved' : 'Denied'}
        </span>
      ),
      sortable: false,
    },
    {
      key: 'added',
      header: 'Added',
      width: '76px',
      accessor: (row) => (
        <div className="text-xs text-[var(--neutral-gray-110)]">
          {row.isNew ? <span className="font-semibold text-[var(--azure-primary)]">New</span> : formatDate(row.firstSeenAt) || '-'}
        </div>
      ),
      sortable: false,
    },
    { key: 'booking', header: 'Booking', accessor: (row) => row.booking || '-', width: '86px' },
    { key: 'last4main', header: 'Main', accessor: (row) => row.last4main || '-', width: '64px' },
    {
      key: 'name',
      header: 'Name',
      accessor: (row) => <button type="button" className="text-left font-medium text-[var(--azure-primary)] hover:underline" onClick={() => setSelectedRow(row)}>{`${row.lastname || ''}, ${row.firstname || ''}`.replace(/^,\s*/, '').trim() || '-'}</button>,
      width: '190px',
      wrap: true,
    },
    { key: 'dob', header: 'DOB', accessor: (row) => formatDate(row.dob) || '-', width: '100px' },
    { key: 'sex', header: 'Sex', accessor: (row) => row.sex || '-', width: '62px' },
    { key: 'caseNumber', header: 'Case', accessor: (row) => row.caseNumber || '-', width: '130px', wrap: true },
    { key: 'facility', header: 'Facility', accessor: (row) => row.facility || '-', width: '96px' },
    { key: 'module', header: 'Module', accessor: (row) => row.module || '-', width: '90px' },
    { key: 'mental', header: 'Mental', accessor: (row) => row.mental || '-', width: '82px' },
    { key: 'arrestDate', header: 'Arrest', accessor: (row) => formatDate(row.arrestDate) || '-', width: '100px' },
    { key: 'plevel', header: 'P-Level', accessor: (row) => row.plevel || '-', width: '58px' },
    {
      key: 'charges',
      header: 'Charges',
      accessor: (row) => (row.charges || []).join(', ') || '-',
      width: '220px',
      wrap: true,
      truncate: true,
      truncateAfterChars: 80,
    },
    {
      key: 'reason',
      header: 'Reason',
      accessor: (row) => formatExclusionReason(row.exclusionReason) || '-',
      width: '190px',
      wrap: true,
      truncate: true,
      truncateAfterChars: 55,
    },
    {
      key: 'actions', header: 'Approval', width: '92px', sortable: false,
      accessor: (row) => <Button type="button" variant="outline" size="sm" className={workflowButtonClass} disabled={decisionRowId === row.id} onClick={() => void handleDecision(row, !row.included)}>{row.included ? <XCircle className="h-4 w-4 shrink-0" /> : <CheckCircle2 className="h-4 w-4 shrink-0" />}{row.included ? 'Deny' : 'Approve'}</Button>,
    },
  ], [decisionRowId]);

  const customizableColumns = allColumns.filter((column) => column.key !== 'actions');
  const effectiveVisibleKeys = visibleColumnKeys.length ? visibleColumnKeys.filter((key) => customizableColumns.some((column) => column.key === key)) : customizableColumns.map((column) => column.key);
  const columns = allColumns.filter((column) => column.key === 'actions' || effectiveVisibleKeys.includes(column.key));

  const updateVisibleColumns = (keys: string[]) => {
    setVisibleColumnKeys(keys);
    localStorage.setItem(COLUMN_STORAGE_KEY, JSON.stringify(keys));
  };

  const handleDecision = async (row: PublicDefendersListRow, included: boolean) => {
    setDecisionRowId(row.id);
    try {
      const response = await setDecision({ variables: { id: row.id, included } });
      const updated = response.data?.setPublicDefendersListDecision;
      toast.success(updated?.exclusionReason || (included ? 'Candidate approved.' : 'Candidate denied.'));
      if (updated) {
        apolloClient.cache.updateQuery(
          { query: PUBLIC_DEFENDERS_LIST_PRE_CANDIDATES, variables: listVariables },
          (cachedData: any) => {
            const cachedList = cachedData?.publicDefendersListPreCandidates;
            if (!Array.isArray(cachedList?.rows)) return cachedData;

            return {
              ...cachedData,
              publicDefendersListPreCandidates: {
                ...cachedList,
                rows: cachedList.rows.map((cachedRow: PublicDefendersListRow) =>
                  cachedRow.id === row.id ? { ...cachedRow, ...updated } : cachedRow
                ),
              },
            };
          }
        );
        if (selectedRow?.id === row.id) setSelectedRow({ ...selectedRow, ...updated });
      }
    } catch (error: any) {
      toast.error(error?.message || 'Unable to update this candidate.');
    } finally {
      setDecisionRowId(null);
    }
  };

  const handleDownload = async () => {
    setExporting(true);
    try {
      const exportRows: PublicDefendersListRow[] = [];
      let exportPage = 1;
      let exportTotal = 0;

      do {
        const response = await apolloClient.query({
          query: PUBLIC_DEFENDERS_LIST_PRE_CANDIDATES,
          variables: {
            dateFrom: dateFrom || null,
            dateTo: dateTo || null,
            page: exportPage,
            pageSize: EXPORT_PAGE_SIZE,
          },
          fetchPolicy: 'network-only',
        });
        const result = response.data?.publicDefendersListPreCandidates;
        const nextRows = Array.isArray(result?.rows) ? result.rows : [];
        exportTotal = Number(result?.totalRows || 0);
        exportRows.push(...nextRows);
        exportPage += 1;

        if (nextRows.length < EXPORT_PAGE_SIZE) break;
      } while (exportRows.length < exportTotal);

      const approvedRows = exportRows.filter((row) => row.included === true);

      if (approvedRows.length === 0) {
        toast.error('There are no approved Public Defenders List rows to download.');
        return;
      }

      const worksheet = XLSX.utils.json_to_sheet(approvedRows.map((row) => ({
        Added: row.isNew ? 'New' : formatDate(row.firstSeenAt),
        Booking: row.booking || '',
        Main: row.last4main || '',
        Name: `${row.lastname || ''}, ${row.firstname || ''}`.replace(/^,\s*/, '').trim(),
        DOB: formatDate(row.dob),
        Sex: row.sex || '',
        Case: row.caseNumber || '',
        Facility: row.facility || '',
        Module: row.module || '',
        Mental: row.mental || '',
        Arrest: formatDate(row.arrestDate),
        'P-Level': row.plevel || '',
        Charges: (row.charges || []).join(', '),
        Reason: formatExclusionReason(row.exclusionReason),
        'HOH Report Date': formatDate(row.hohReportDate),
        'P-Level Report Date': formatDate(row.pLevelReportDate),
        'Decision By': row.decisionByUsername || '',
        'Decision Date': formatDate(row.decisionAt),
      })));
      worksheet['!cols'] = [
        { wch: 12 }, { wch: 12 }, { wch: 8 }, { wch: 28 },
        { wch: 12 }, { wch: 6 }, { wch: 18 }, { wch: 14 }, { wch: 12 },
        { wch: 10 }, { wch: 12 }, { wch: 8 }, { wch: 42 }, { wch: 55 },
        { wch: 16 }, { wch: 18 }, { wch: 20 }, { wch: 18 },
      ];
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Public Defenders List');
      const fileSuffix = `${dateFrom || 'start'}_to_${dateTo || 'current'}`.replace(/[^0-9A-Za-z_-]/g, '');
      XLSX.writeFile(workbook, `public_defenders_list_${fileSuffix}.xls`, { bookType: 'biff8' });
      toast.success(`Downloaded ${approvedRows.length} approved rows.`);
    } catch (error: any) {
      toast.error(error?.message || 'Unable to download the Public Defenders List.');
    } finally {
      setExporting(false);
    }
  };

  const handleWorkbookFile = async (
    file: File | undefined,
    setWorkbook: (workbook: SheetWorkbook | null) => void,
    setSheet: (sheet: string) => void,
    preferLatestPLevelSheet = false,
  ) => {
    if (!file) {
      setWorkbook(null);
      setSheet('');
      return;
    }

    try {
      const nextWorkbook = await readWorkbook(file);
      setWorkbook(nextWorkbook);
      const preferredSheet = preferLatestPLevelSheet
        ? nextWorkbook.sheetNames.filter((sheet) => normalizeHeader(sheet).includes('plevelreport')).slice(-1)[0]
        : '';
      setSheet(preferredSheet || nextWorkbook.sheetNames[0] || '');
    } catch (error: any) {
      toast.error(error?.message || 'Unable to read workbook.');
      setWorkbook(null);
      setSheet('');
    }
  };

  const handleProcess = async () => {
    if (!hohWorkbook || !pLevelWorkbook || !hohSheet || !pLevelSheet) {
      toast.error('Select both workbooks and sheets.');
      return;
    }

    setProcessing(true);
    try {
      const candidates = parseAndScreenCandidates({
        hohWorkbook,
        hohSheet,
        pLevelWorkbook,
        pLevelSheet,
        chargeRules,
      });

      if (candidates.length === 0) {
        toast.error('No rows with booking numbers were found.');
        return;
      }

      const hohReportDate = reportDateFromName(hohWorkbook.fileName) || new Date().toISOString().slice(0, 10);
      const pLevelReportDate = reportDateFromName(pLevelSheet) || reportDateFromName(pLevelWorkbook.fileName) || hohReportDate;
      const response = await processReports({
        variables: {
          input: {
            hohReportDate,
            pLevelReportDate,
            rows: candidates,
          },
        },
      });

      const result = response.data?.processPublicDefendersListPreCandidates;
      toast.success(result?.message || `Processed ${candidates.length} rows.`);
      setUploadOpen(false);
      await refetch(listVariables);
    } catch (error: any) {
      toast.error(error?.message || 'Unable to process reports.');
    } finally {
      setProcessing(false);
    }
  };

  const resetDialog = () => {
    if (processing) return;
    setHohWorkbook(null);
    setPLevelWorkbook(null);
    setHohSheet('');
    setPLevelSheet('');
  };

  return (
    <div className="px-0 pb-4 pt-0 sm:px-1 lg:px-1 xl:px-2">
      <div className="mb-3 flex flex-wrap items-end justify-between gap-4 rounded-[var(--border-radius-medium)] border border-[var(--neutral-gray-40)] bg-white px-4 py-3">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="public-defenders-date-from" className="text-xs font-semibold uppercase tracking-wide text-[var(--neutral-gray-100)]">From</Label>
            <Input
              id="public-defenders-date-from"
              type="date"
              value={dateFrom}
              onChange={(event) => {
                setDateFrom(event.target.value);
                setPage(1);
              }}
              className="h-8 w-[150px]"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="public-defenders-date-to" className="text-xs font-semibold uppercase tracking-wide text-[var(--neutral-gray-100)]">To</Label>
            <Input
              id="public-defenders-date-to"
              type="date"
              value={dateTo}
              onChange={(event) => {
                setDateTo(event.target.value);
                setPage(1);
              }}
              className="h-8 w-[150px]"
            />
          </div>
          <Button type="button" variant="outline" size="sm" className="h-8" onClick={() => refetch(listVariables)} disabled={loading}>
            <RefreshCw className="h-4 w-4" />
            Refresh
          </Button>
        </div>

        <div className="flex items-center gap-2">
          <Button type="button" size="sm" className="h-8" onClick={() => setUploadOpen(true)}>
            <Upload className="h-4 w-4" />
            Upload Reports
          </Button>
          <Button type="button" variant="outline" size="sm" className="h-8" onClick={() => void handleDownload()} disabled={exporting}>
            <Download className="h-4 w-4" />
            {exporting ? 'Downloading...' : 'Download XLS'}
          </Button>
        </div>
      </div>

      <UnifiedTable<PublicDefendersListRow>
        columns={columns}
        data={rows}
        rowKey={(row) => String(row.id)}
        className="h-[66vh]"
        loading={loading}
        emptyTitle="No public defenders list entries found"
        emptyDescription="Try changing the date range."
        toolbarLeft={
          <div className="flex items-center gap-2 text-xs text-[var(--neutral-gray-100)]">
            <AlertCircle className="h-4 w-4" />
            <span>{totalRows} rows</span>
          </div>
        }
        toolbarRight={
          <div className="flex flex-wrap items-center justify-end gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild><Button type="button" variant="outline" size="sm" className="h-8"><Settings2 className="h-3.5 w-3.5" /> Columns</Button></DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="max-h-[70vh] w-60 overflow-y-auto">
                <DropdownMenuLabel>Show or Hide Columns</DropdownMenuLabel><DropdownMenuSeparator />
                {customizableColumns.map((column) => {
                  const checked = effectiveVisibleKeys.includes(column.key);
                  return <DropdownMenuCheckboxItem key={column.key} checked={checked} disabled={checked && effectiveVisibleKeys.length === 1} onCheckedChange={(next) => updateVisibleColumns(next ? [...effectiveVisibleKeys, column.key] : effectiveVisibleKeys.filter((key) => key !== column.key))}>{column.header}</DropdownMenuCheckboxItem>;
                })}
              </DropdownMenuContent>
            </DropdownMenu>
            <div className="flex items-center gap-1">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 w-8 p-0"
                disabled={!canGoBack}
                onClick={() => setPage((current) => Math.max(1, current - 1))}
                aria-label="Previous page"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="min-w-[80px] text-center text-xs text-[var(--neutral-gray-100)]">
                {page} / {totalPages}
              </span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 w-8 p-0"
                disabled={!canGoNext}
                onClick={() => setPage((current) => current + 1)}
                aria-label="Next page"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
            <select
              value={String(pageSize)}
              onChange={(event) => {
                setPageSize(Number(event.target.value || DEFAULT_PAGE_SIZE));
                setPage(1);
              }}
              className="h-8 w-[90px] rounded-[var(--border-radius-medium)] border border-[var(--neutral-gray-60)] bg-white px-2 text-xs text-[var(--neutral-gray-120)]"
              aria-label="Rows per page"
            >
              {[20, 50, 100].map((size) => (
                <option key={size} value={size}>
                  {size}/page
                </option>
              ))}
            </select>
          </div>
        }
      />

      <Dialog open={selectedRow != null} onOpenChange={(open: boolean) => { if (!open) setSelectedRow(null); }}>
        <DialogContent className="max-h-[90vh] overflow-hidden p-0" contentWidth="80vw" contentMaxWidth="80vw" contentHeight="80vh">
          {selectedRow ? <div className="flex h-full min-h-0 flex-col">
            <DialogHeader className="shrink-0 border-b border-[var(--neutral-gray-40)] px-6 py-4">
              <DialogTitle className="text-left">{selectedRow.lastname}, {selectedRow.firstname}</DialogTitle>
            </DialogHeader>
            <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
              <dl className="grid grid-cols-1 gap-x-8 sm:grid-cols-2 lg:grid-cols-3">
                {[
                  ['Status', selectedRow.included ? 'Approved' : 'Denied'], ['Reason', formatExclusionReason(selectedRow.exclusionReason) || '—'],
                  ['Booking', selectedRow.booking], ['Main', selectedRow.last4main], ['DOB', formatDate(selectedRow.dob)],
                  ['Sex', selectedRow.sex], ['Case', selectedRow.caseNumber], ['Facility', selectedRow.facility],
                  ['Module', selectedRow.module], ['Mental', selectedRow.mental], ['Arrest Date', formatDate(selectedRow.arrestDate)],
                  ['P-Level', selectedRow.plevel], ['HOH Report', formatDate(selectedRow.hohReportDate)], ['P-Level Report', formatDate(selectedRow.pLevelReportDate)],
                ].map(([label, value]) => <div key={label || ''} className="grid min-w-0 grid-cols-[7rem_minmax(0,1fr)] items-baseline gap-2 py-1.5"><dt className="text-[11px] font-semibold uppercase tracking-wide text-[var(--neutral-gray-90)]">{label}</dt><dd className="whitespace-pre-wrap break-words text-sm text-[var(--neutral-gray-120)]">{value || '—'}</dd></div>)}
              </dl>
              {(selectedRow.cases || []).length > 0 && <div className="mt-4"><span className="text-xs font-semibold uppercase tracking-wide text-[var(--neutral-gray-90)]">Cases </span><span className="ml-3 text-sm">{selectedRow.cases!.join(', ')}</span></div>}
              <div className="mt-4">
                <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-[var(--neutral-gray-90)]">Charges (full text)</h3>
                {(selectedRow.charges || []).length ? <ul className="space-y-1.5">{selectedRow.charges!.map((charge, index) => <li key={`${charge}-${index}`} className="whitespace-pre-wrap break-words text-sm text-[var(--neutral-gray-120)]">{charge}</li>)}</ul> : <span className="text-sm text-[var(--neutral-gray-90)]">No charges recorded.</span>}
              </div>
            </div>
            <div className="flex shrink-0 items-center justify-between border-t border-[var(--neutral-gray-40)] px-6 py-3">
              <span className="text-xs text-[var(--neutral-gray-90)]">{selectedRow.decisionByUsername ? `Last decision by ${selectedRow.decisionByUsername}` : 'System decision'}</span>
              <div className="flex items-center gap-2">
                <Button type="button" variant="outline" size="sm" className={workflowButtonClass} disabled={decisionRowId === selectedRow.id} onClick={() => void handleDecision(selectedRow, !selectedRow.included)}>{selectedRow.included ? <XCircle className="h-4 w-4 shrink-0" /> : <CheckCircle2 className="h-4 w-4 shrink-0" />}{selectedRow.included ? 'Deny' : 'Approve'}</Button>
                <Button type="button" size="sm" variant="outline" onClick={() => setSelectedRow(null)}>Close</Button>
              </div>
            </div>
          </div> : null}
        </DialogContent>
      </Dialog>

      <Dialog
        open={uploadOpen}
        onOpenChange={(open: boolean) => {
          if (!open) resetDialog();
          setUploadOpen(open);
        }}
      >
        <DialogContent className="max-h-[86vh] overflow-hidden p-0" contentWidth="760px" contentMaxWidth="760px" contentHeight="auto">
          <div className="flex min-h-0 flex-col">
            <DialogHeader className="shrink-0 border-b border-[var(--neutral-gray-40)] px-5 py-4">
              <DialogTitle className="text-left text-lg font-semibold text-[var(--neutral-gray-120)]">
                Public Defenders List Upload
              </DialogTitle>
            </DialogHeader>

            <div className="grid gap-4 px-5 py-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="hoh-report">HOH Report</Label>
                <Input
                  id="hoh-report"
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={(event) => void handleWorkbookFile(event.target.files?.[0], setHohWorkbook, setHohSheet)}
                />
                {hohWorkbook && (
                  <select
                    value={hohSheet}
                    onChange={(event) => setHohSheet(event.target.value)}
                    className="h-9 w-full rounded-[var(--border-radius-medium)] border border-[var(--neutral-gray-60)] bg-white px-2 text-sm text-[var(--neutral-gray-120)]"
                  >
                    {hohWorkbook.sheetNames.map((sheet) => (
                      <option key={sheet} value={sheet}>{sheet}</option>
                    ))}
                  </select>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="plevel-report">P-Level Report</Label>
                <Input
                  id="plevel-report"
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={(event) => void handleWorkbookFile(event.target.files?.[0], setPLevelWorkbook, setPLevelSheet, true)}
                />
                {pLevelWorkbook && (
                  <select
                    value={pLevelSheet || defaultPLevelSheet}
                    onChange={(event) => setPLevelSheet(event.target.value)}
                    className="h-9 w-full rounded-[var(--border-radius-medium)] border border-[var(--neutral-gray-60)] bg-white px-2 text-sm text-[var(--neutral-gray-120)]"
                  >
                    {pLevelWorkbook.sheetNames.map((sheet) => (
                      <option key={sheet} value={sheet}>{sheet}</option>
                    ))}
                  </select>
                )}
              </div>
            </div>

            <div className="shrink-0 flex items-center justify-between gap-3 border-t border-[var(--neutral-gray-40)] px-5 py-3">
              <div className="text-xs text-[var(--neutral-gray-90)]">
                {chargeRules.length} bad charge rules loaded
              </div>
              <div className="flex items-center gap-2">
                <Button type="button" size="sm" onClick={() => void handleProcess()} disabled={processing}>
                  {processing ? 'Processing...' : 'Start Process'}
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={() => setUploadOpen(false)} disabled={processing}>
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PublicDefendersListPage;
